import { useEffect } from 'react';
import { InputAdornment, Grid } from '@mui/material';
import { useForm } from 'react-final-form';

import { Typography } from '@occc/ui-design-system';
import {
  ToggleButtonGroupField,
  CurrencyInputField,
  TextField,
  ProductGroupSelectField,
} from '@occc-app/design-system';
import { parseNumber } from '@occc-app/lib/finalFormUtils';

import { VarietyDiscountPurchasesConfiguration } from './VarietyDiscountPurchasesConfiguration';

import {
  VarietyDiscountAwardType,
  ProductDiscountAwardType,
  VarietyDiscountFormValues,
} from '../types';

const awardTypeButtons = [
  { title: 'Transaction Discount', value: VarietyDiscountAwardType.TransactionDiscount },
  { title: 'Product Discount', value: VarietyDiscountAwardType.ProductDiscount },
  { title: 'Price Override', value: VarietyDiscountAwardType.PriceOverride },
];

const productDiscountAwardOptions = [
  {
    title: `Free`,
    value: ProductDiscountAwardType.Free,
  },
  { title: 'Discount', value: ProductDiscountAwardType.Discount },
];

type VarietyDiscountConfigurationProps = {
  values: VarietyDiscountFormValues;
};

type VarietyDiscountProductDiscountValues = Extract<
  VarietyDiscountFormValues,
  { awardType: VarietyDiscountAwardType.ProductDiscount }
>;

type IncompleteProductDiscount = {
  awardType?: ProductDiscountAwardType;
  eligibleProductGroups?: { id: string; name: string }[];
  purchaseThreshold?: number;
  percentDiscount?: number;
};

export const VarietyDiscountConfiguration = ({ values }: VarietyDiscountConfigurationProps) => {
  const form = useForm();

  useEffect(() => {
    if (values.awardType !== VarietyDiscountAwardType.ProductDiscount) {
      return;
    }

    const productDiscount = (values as VarietyDiscountProductDiscountValues).productDiscount as
      | IncompleteProductDiscount
      | undefined;

    if (productDiscount?.awardType) {
      return;
    }

    form.change('varietyDiscount.productDiscount', {
      ...productDiscount,
      awardType: ProductDiscountAwardType.Free,
      eligibleProductGroups: productDiscount?.eligibleProductGroups ?? [],
    });
  }, [values, form]);

  const renderMoneyDiscount = () => {
    return (
      <CurrencyInputField
        name='varietyDiscount.transactionDiscount.moneyDiscount'
        label='Dollars off'
      />
    );
  };

  const renderPurchaseThreshold = () => {
    return (
      <TextField
        name='varietyDiscount.productDiscount.purchaseThreshold'
        label='Items'
        fieldProps={{
          parse: parseNumber,
        }}
      />
    );
  };

  const renderPercentDiscount = () => {
    return (
      <TextField
        name='varietyDiscount.productDiscount.percentDiscount'
        fieldProps={{
          parse: parseNumber,
        }}
        label='Percent off'
        InputProps={{
          endAdornment: <InputAdornment position='end'>%</InputAdornment>,
        }}
      />
    );
  };

  return (
    <Grid container spacing={3}>
      <Grid item>
        <Typography variant='subtitle1' paragraph>
          Member Receive
        </Typography>

        <Grid container spacing={3}>
          <Grid item>
            <ToggleButtonGroupField name='varietyDiscount.awardType' buttons={awardTypeButtons} />
          </Grid>

          {values?.awardType === VarietyDiscountAwardType.TransactionDiscount && (
            <Grid item xs={3}>
              {renderMoneyDiscount()}
            </Grid>
          )}

          {values?.awardType === VarietyDiscountAwardType.ProductDiscount && (
            <Grid item>
              <ToggleButtonGroupField
                fitToContent
                name='varietyDiscount.productDiscount.awardType'
                buttons={productDiscountAwardOptions}
              />
            </Grid>
          )}

          {values?.awardType === VarietyDiscountAwardType.PriceOverride && (
            <Grid item xs={3}>
              <CurrencyInputField
                name='varietyDiscount.priceOverride.maximumCost'
                label='Money Amount'
              />
            </Grid>
          )}
        </Grid>
      </Grid>

      {values?.awardType === VarietyDiscountAwardType.ProductDiscount && (
        <>
          {(values?.productDiscount?.awardType === ProductDiscountAwardType.Discount ||
            values?.productDiscount?.awardType === ProductDiscountAwardType.Free) && (
            <Grid item xs={12}>
              <Typography variant='subtitle1' paragraph>
                For Every
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={2}>
                  {renderPurchaseThreshold()}
                </Grid>
                {values?.productDiscount?.awardType === ProductDiscountAwardType.Discount && (
                  <Grid item xs={3}>
                    {renderPercentDiscount()}
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant='subtitle1' paragraph>
              On the following product group(s):
            </Typography>
            <ProductGroupSelectField
              inputProps={{
                margin: 'none',
              }}
              name='varietyDiscount.productDiscount.eligibleProductGroups'
              label='Product groups'
            />
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <VarietyDiscountPurchasesConfiguration />
      </Grid>
    </Grid>
  );
};
